
export const isValid = (grid: (number | null)[][], row: number, col: number, num: number): boolean => {
  for (let x = 0; x < 9; x++) if (grid[row][x] === num) return false;
  for (let x = 0; x < 9; x++) if (grid[x][col] === num) return false;
  let startRow = row - (row % 3), startCol = col - (col % 3);
  for (let i = 0; i < 3; i++)
    for (let j = 0; j < 3; j++)
      if (grid[i + startRow][j + startCol] === num) return false;
  return true;
};

const solve = (grid: (number | null)[][]): boolean => {
  for (let row = 0; row < 9; row++) {
    for (let col = 0; col < 9; col++) {
      if (grid[row][col] === null) {
        for (let num = 1; num <= 9; num++) {
          if (isValid(grid, row, col, num)) {
            grid[row][col] = num;
            if (solve(grid)) return true;
            grid[row][col] = null;
          }
        }
        return false;
      }
    }
  }
  return true;
};

/**
 * Generates a Sudoku puzzle with "near-complete" structures
 * designed for combo scoring in Sudoktro.
 */
export const generatePuzzle = (difficulty: 'EASY' | 'MEDIUM' | 'HARD' = 'MEDIUM', stage: number = 1) => {
  const fullGrid: (number | null)[][] = Array.from({ length: 9 }, () => Array(9).fill(null));
  
  // 1. Generate full solved board
  for (let i = 0; i < 9; i += 3) {
    const nums = [1, 2, 3, 4, 5, 6, 7, 8, 9].sort(() => Math.random() - 0.5);
    let idx = 0;
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 3; col++) {
        fullGrid[i + row][i + col] = nums[idx++];
      }
    }
  }
  solve(fullGrid);
  const solution = JSON.parse(JSON.stringify(fullGrid));
  
  // 2. Determine clue count based on difficulty and stage
  let baseClues = 40;
  let stageDrain = 2;
  
  if (difficulty === 'EASY') {
    baseClues = 56; // High number of initial clues for Easy
    stageDrain = 1.2; // Very slow clue drain for Easy
  } else if (difficulty === 'MEDIUM') {
    baseClues = 44;
    stageDrain = 2;
  } else {
    baseClues = 34;
    stageDrain = 3;
  }
  
  const clueCount = Math.floor(Math.max(26, baseClues - (stage * stageDrain)));
  const cellsToRemove = 81 - clueCount;

  // 3. Create "Combo Opportunities" (Near-complete structures)
  const puzzle: (number | null)[][] = JSON.parse(JSON.stringify(fullGrid));
  const protectedCells = new Set<string>();

  // Increase protection for Easy difficulty to ensure more structures are ready for completion
  const bonusStructures = difficulty === 'EASY' ? 2 : 0;
  const rowsToKeep = Array.from({length: 2 + bonusStructures}, () => Math.floor(Math.random() * 9));
  const colsToKeep = Array.from({length: 2 + bonusStructures}, () => Math.floor(Math.random() * 9));
  
  rowsToKeep.forEach(r => {
    const indices = [0,1,2,3,4,5,6,7,8].sort(() => Math.random() - 0.5).slice(0, 8);
    indices.forEach(c => protectedCells.add(`${r},${c}`));
  });

  colsToKeep.forEach(c => {
    const indices = [0,1,2,3,4,5,6,7,8].sort(() => Math.random() - 0.5).slice(0, 8);
    indices.forEach(r => protectedCells.add(`${r},${c}`));
  });

  // 4. Remove the rest randomly up to clue count
  let removed = 0;
  const allCells: [number, number][] = [];
  for(let r=0; r<9; r++) for(let c=0; c<9; c++) allCells.push([r, c]);
  allCells.sort(() => Math.random() - 0.5);

  for (const [r, c] of allCells) {
    if (removed >= cellsToRemove) break;
    if (protectedCells.has(`${r},${c}`)) continue;
    if (puzzle[r][c] !== null) {
      puzzle[r][c] = null;
      removed++;
    }
  }
  
  return { puzzle, solution };
};
